import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { firstValueFrom, Subscription } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { TopicEvidenceModePreviewResponse, TopicEvidenceModeReviewRow } from '../../../../models';
import { CareerRoleAdminActionsService } from '../shared/career-role-admin-actions.service';
import { CareerRoleFullPictureStore } from '../shared/career-role-full-picture.store';
import { findCareerRoleSlugRoute } from '../shared/career-role-route.util';

@Component({
  selector: 'app-topic-evidence-modes-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatProgressBarModule],
  template: `
    <section class="evidence-review">
      <h2>Topic evidence modes</h2>
      <p>Implementation needs working artifacts; conceptual needs explanation and reasoning; mixed needs both.</p>
      <p>Generate suggestions for all core and specialization topics in one Bedrock batch job for this role.
        Review each mode, edit if needed, then commit the whole role. Existing confirmed modes remain selected unless you change them.</p>
      <div class="actions">
        <button mat-flat-button *ngIf="state?.status === 'failed' && state?.batchJobId"
          (click)="reprocess()" [disabled]="busy">Reprocess existing batch (no new AI call)</button>
        <button mat-stroked-button (click)="generate()" [disabled]="busy || pending || !rows.length">
          {{ state?.status === 'ready' ? 'Replace review with new AI batch' : 'Generate suggestions (AI batch)' }}
        </button>
        <button mat-button (click)="load()" [disabled]="busy || dirty">Refresh status</button>
        <button mat-stroked-button (click)="save()" [disabled]="busy || !canCommit || !dirty">Save review</button>
        <button mat-flat-button (click)="commit()" [disabled]="busy || !canCommit">Commit all {{ rows.length }} topics</button>
      </div>
      <mat-progress-bar *ngIf="busy || pending" mode="indeterminate"></mat-progress-bar>
      <p role="status">{{ statusLabel }} <span *ngIf="dirty">You have unsaved review edits.</span></p>
      <p *ngIf="pending">The job continues if you leave this page. Status refreshes every 30 seconds.</p>
      <p *ngIf="state?.status === 'failed' && state?.batchJobId">Reprocessing reads the existing batch output again.
        Generate suggestions submits a new AI batch.</p>
      <p *ngIf="state?.status === 'ready'">Save review keeps your edits for later. Commit all applies every topic, including topics hidden by filters.
        Generating a new batch replaces this review and its edits.</p>
      <p *ngIf="message" role="status">{{ message }}</p>
      <p *ngIf="error || state?.failureReason" class="error" role="alert">{{ error || state?.failureReason }}</p>
      <div class="filters">
        <label><input type="checkbox" [(ngModel)]="onlyKeyMismatches" (ngModelChange)="page = 0">
          Only altered AI keys ({{ mismatchedKeyCount }})</label>
        <mat-form-field appearance="outline">
          <mat-label>Search skill or topic</mat-label>
          <input matInput [(ngModel)]="query" (ngModelChange)="page = 0">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Seniority</mat-label>
          <mat-select [(ngModel)]="seniority" (ngModelChange)="page = 0">
            <mat-option value="">All levels</mat-option>
            <mat-option *ngFor="let level of levels" [value]="level">{{ level }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Scope</mat-label>
          <mat-select [(ngModel)]="scope" (ngModelChange)="page = 0">
            <mat-option value="">All scopes</mat-option>
            <mat-option *ngFor="let item of scopes" [value]="item">{{ item }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Review mode</mat-label>
          <mat-select [(ngModel)]="mode" (ngModelChange)="page = 0">
            <mat-option value="">All modes</mat-option>
            <mat-option value="implementation">Implementation</mat-option>
            <mat-option value="conceptual">Conceptual</mat-option>
            <mat-option value="mixed">Mixed</mat-option>
            <mat-option value="unclassified">Unclassified</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <p>{{ filteredRows.length }} matching topics of {{ rows.length }} total.</p>
      <div class="table-wrap">
        <table>
          <thead><tr><th>Topic</th><th>Profile</th><th>Current mode</th><th>AI suggestion</th><th>Review mode</th></tr></thead>
          <tbody>
            <tr *ngFor="let row of visibleRows; trackBy: trackRow">
              <td><strong>{{ row.topicName }}</strong><br>{{ row.skillName }}</td>
              <td>{{ row.specializationSlug || 'Core' }}<br>{{ row.seniority }} · {{ row.requiredProficiency }}</td>
              <td>{{ row.currentEvidenceMode || 'Unclassified' }}</td>
              <td>{{ row.suggestedEvidenceMode || '—' }}<p>{{ row.rationale }}</p>
                <p *ngIf="row.aiKeyMismatch">AI changed the topic key. This suggestion was matched using its batch record ID; check the rationale before committing.</p>
                <button mat-button *ngIf="row.suggestedEvidenceMode && row.suggestedEvidenceMode !== row.evidenceMode"
                  [disabled]="busy || state?.status !== 'ready'" (click)="useSuggestion(row)">Use suggestion</button>
              </td>
              <td>
                <mat-form-field appearance="outline">
                  <mat-label>Mode for {{ row.topicName }}</mat-label>
                  <mat-select [(ngModel)]="row.evidenceMode" (ngModelChange)="dirty = true"
                    [disabled]="busy || state?.status !== 'ready'">
                    <mat-option [value]="null" disabled>Unclassified</mat-option>
                    <mat-option value="implementation">Implementation</mat-option>
                    <mat-option value="conceptual">Conceptual</mat-option>
                    <mat-option value="mixed">Mixed</mat-option>
                  </mat-select>
                </mat-form-field>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="actions" *ngIf="filteredRows.length > pageSize">
        <button mat-button (click)="page = page - 1" [disabled]="page === 0">Previous</button>
        <span>Page {{ page + 1 }} of {{ pageCount }}</span>
        <button mat-button (click)="page = page + 1" [disabled]="page + 1 >= pageCount">Next</button>
      </div>
    </section>
  `,
  styles: [`
    .evidence-review { padding: 1.5rem; }
    .actions, .filters { display: flex; align-items: center; flex-wrap: wrap; gap: 1rem; margin: 1rem 0; }
    .table-wrap { overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; text-align: left; }
    th, td { padding: .8rem; border-bottom: 1px solid #d7dde5; vertical-align: top; }
    td p { max-width: 24rem; margin: .4rem 0; }
    td mat-form-field { min-width: 190px; }
    .error { color: #b42318; }
  `]
})
export class TopicEvidenceModesTabComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly store = inject(CareerRoleFullPictureStore);
  private subscription?: Subscription;
  private timer?: ReturnType<typeof setInterval>;
  slug = '';
  state?: TopicEvidenceModePreviewResponse;
  rows: TopicEvidenceModeReviewRow[] = [];
  busy = false;
  dirty = false;
  error = '';
  message = '';
  query = '';
  seniority = '';
  scope = '';
  mode = '';
  onlyKeyMismatches = false;
  page = 0;
  readonly pageSize = 50;

  get pending(): boolean { return ['queued', 'submitting', 'awaiting_batch', 'committing'].includes(this.state?.status ?? ''); }
  get canCommit(): boolean { return this.state?.status === 'ready' && this.rows.length > 0 && this.rows.every(row => !!row.evidenceMode); }
  get levels(): string[] { return [...new Set(this.rows.map(row => row.seniority))]; }
  get scopes(): string[] { return [...new Set(this.rows.map(row => row.specializationSlug || 'Core'))]; }
  get mismatchedKeyCount(): number { return this.rows.filter(row => row.aiKeyMismatch).length; }
  get filteredRows(): TopicEvidenceModeReviewRow[] {
    const query = this.query.trim().toLowerCase();
    return this.rows.filter(row => (!query || `${row.skillName} ${row.topicName}`.toLowerCase().includes(query))
      && (!this.seniority || row.seniority === this.seniority)
      && (!this.scope || (row.specializationSlug || 'Core') === this.scope)
      && (!this.mode || (row.evidenceMode || 'unclassified') === this.mode)
      && (!this.onlyKeyMismatches || row.aiKeyMismatch));
  }
  get pageCount(): number { return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize)); }
  get visibleRows(): TopicEvidenceModeReviewRow[] {
    const page = Math.min(this.page, this.pageCount - 1);
    return this.filteredRows.slice(page * this.pageSize, (page + 1) * this.pageSize);
  }
  get statusLabel(): string {
    const labels: Record<string, string> = { idle: 'No batch generated yet.', queued: 'Queued for submission.',
      submitting: 'Submitting to Bedrock.', awaiting_batch: 'Bedrock is processing suggestions.', ready: 'Ready for review.',
      committing: 'Applying reviewed modes.', committed: 'All reviewed modes committed.', failed: 'Batch processing failed. Reprocess existing output if available.' };
    return labels[this.state?.status ?? 'idle'];
  }

  ngOnInit(): void {
    this.subscription = findCareerRoleSlugRoute(this.route)?.paramMap.subscribe(params => {
      this.slug = params.get('slug') ?? '';
      this.dirty = false;
      this.state = undefined;
      this.rows = [];
      this.page = 0;
      this.busy = false;
      this.error = '';
      this.message = '';
      this.query = this.seniority = this.scope = this.mode = '';
      this.onlyKeyMismatches = false;
      if (this.slug) void this.load();
    });
    this.timer = setInterval(() => { if (this.pending && !this.busy) void this.load(); }, 30000);
  }
  ngOnDestroy(): void { this.subscription?.unsubscribe(); if (this.timer) clearInterval(this.timer); }
  trackRow(_index: number, row: TopicEvidenceModeReviewRow): string { return row.key; }
  useSuggestion(row: TopicEvidenceModeReviewRow): void { row.evidenceMode = row.suggestedEvidenceMode ?? row.evidenceMode; this.dirty = true; }
  private accept(response: TopicEvidenceModePreviewResponse): void { this.state = response; this.rows = response.suggestions; this.dirty = false; }

  async load(): Promise<void> {
    const slug = this.slug;
    this.busy = true;
    this.error = '';
    try { const response = await firstValueFrom(this.api.getTopicEvidenceModes(slug)); if (this.slug === slug) this.accept(response); }
    catch (error) { if (this.slug === slug) this.error = this.actions.errorMessage(error); }
    finally { if (this.slug === slug) this.busy = false; }
  }
  async generate(): Promise<void> {
    if (this.busy || this.pending) return;
    const slug = this.slug;
    this.busy = true;
    this.error = '';
    this.message = '';
    try { const response = await firstValueFrom(this.api.suggestTopicEvidenceModes(slug)); if (this.slug === slug) this.accept(response); }
    catch (error) { if (this.slug === slug) this.error = this.actions.errorMessage(error); }
    finally { if (this.slug === slug) this.busy = false; }
  }
  async reprocess(): Promise<void> {
    if (this.busy || this.state?.status !== 'failed' || !this.state.reviewId) return;
    const slug = this.slug;
    this.busy = true;
    this.error = '';
    this.message = '';
    try {
      const response = await firstValueFrom(this.api.reprocessTopicEvidenceModes(slug, this.state.reviewId));
      if (this.slug === slug) {
        this.accept(response);
        if (response.status === 'ready') this.message = 'Existing batch results recovered. Review the suggestions before committing.';
      }
    } catch (error) { if (this.slug === slug) this.error = this.actions.errorMessage(error); }
    finally { if (this.slug === slug) this.busy = false; }
  }
  async save(): Promise<void> {
    if (!this.canCommit || this.busy || !this.state?.reviewId) return;
    const slug = this.slug;
    this.busy = true;
    this.error = '';
    try {
      const response = await firstValueFrom(this.api.saveTopicEvidenceModeReview(slug, this.state.reviewId, this.rows));
      if (this.slug === slug) {
        this.accept(response);
        this.message = 'Review saved. Catalog modes will change when you commit.';
      }
    } catch (error) { if (this.slug === slug) this.error = this.actions.errorMessage(error); }
    finally { if (this.slug === slug) this.busy = false; }
  }
  async commit(): Promise<void> {
    if (!this.canCommit || this.busy || !this.state?.reviewId) return;
    const slug = this.slug;
    const total = this.rows.length;
    this.busy = true;
    this.error = '';
    try {
      const count = await firstValueFrom(this.api.confirmTopicEvidenceModes(slug, this.state.reviewId, this.rows));
      this.store.invalidate(slug, { refresh: true });
      if (this.slug !== slug) return;
      this.dirty = false;
      this.message = `Committed all ${total} reviewed topics; ${count} modes changed.`;
      await this.load();
    } catch (error) {
      this.store.invalidate(slug, { refresh: true });
      if (this.slug === slug) this.error = this.actions.errorMessage(error);
    } finally { if (this.slug === slug) this.busy = false; }
  }
}
